import type { Tab } from '@/atoms/sessionWindowsAtoms';
import { usePendingTabFocus } from '@/hooks/query/useQuerySessionWindows';
import { artifactPanelMessages } from '@heidi/agents/data';
import { toast } from '@heidi/design-system/toast';
import { useBuildSessionPath } from '@heidi/session';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

/**
 * Leave a Work preview for the real session page. Stages tab intent first
 * (when a tab is given) so the page lands on the chip's surface instead of
 * the session default. A missing sessionId is a no-op — patient chips use a
 * different open path.
 *
 * Lives app-side (router + toast). The projects root is locked, so this
 * cannot be a new export under ``src/features/projects``.
 */
export const useOpenSessionPage = (
  sessionId: string | undefined,
  tab?: Tab
): (() => void) => {
  const intl = useIntl();
  const router = useRouter();
  const buildSessionPath = useBuildSessionPath();
  const { requestTabFocus, clearTabFocus } = usePendingTabFocus({
    sessionId: sessionId ?? '',
  });

  return useCallback(() => {
    if (!sessionId) return;

    const fail = () => {
      if (tab) clearTabFocus(tab);
      toast({
        tone: 'destructive',
        title: intl.formatMessage(artifactPanelMessages.sessionNavigationFailed),
      });
    };

    if (tab) requestTabFocus(tab);
    void router.push(buildSessionPath(sessionId)).then((didNavigate) => {
      if (!didNavigate) fail();
    }, fail);
  }, [buildSessionPath, clearTabFocus, intl, requestTabFocus, router, sessionId, tab]);
};
